import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import type { Client, PostureAnalysis } from '@/lib/types';
import { HeightDisplay, WeightDisplay } from '@/components/ui/unit-display';
import { calculateBMI, getBMICategory, getBMICategoryColor } from '@/components/clients/lib/bmi-calculator';
import { ClientClassificationBadges } from '@/components/clients/ClientClassificationBadges';
import type { CachedPostureAnalysis } from '@/context/clients-cache-context';

interface ClientOverviewTabProps {
  client: Client;
  assessmentCount: number;
  postureCount: number;
  programCount: number;
  noteCount: number;
  allPostureAnalyses?: CachedPostureAnalysis[];
  /** This client's own posture-analysis history (already loaded by the Client 360 page). */
  postureAnalyses?: PostureAnalysis[];
}

export function ClientOverviewTab({
  client,
  assessmentCount,
  postureCount,
  programCount,
  noteCount,
  allPostureAnalyses,
}: ClientOverviewTabProps) {
  const bmi =
    client.height && client.weight ? calculateBMI(client.height, client.weight) : 0;
  const bmiCategory = bmi > 0 ? getBMICategory(bmi) : '';
  const bmiColor = bmi > 0 ? getBMICategoryColor(bmi) : '';

  return (
    <div className="space-y-3 sm:space-y-4" data-testid="client-overview-tab">
      {/* Compact 2x2 grid on mobile (4-up from md), small padding + number size —
          this was the "biggest remaining tall" panel on Client 360. */}
      <div className="grid grid-cols-2 gap-2 md:grid-cols-4">
        {[
          { label: 'Assessments', value: assessmentCount },
          { label: 'Posture', value: postureCount },
          { label: 'Programs', value: programCount },
          { label: 'Notes', value: noteCount },
        ].map((stat) => (
          <Card key={stat.label} className="border-none shadow-sm">
            <CardContent className="p-2.5 sm:p-3">
              <p className="text-[10px] font-medium uppercase tracking-wide text-muted-foreground">
                {stat.label}
              </p>
              <p className="mt-0.5 text-xl font-bold leading-none sm:text-2xl">{stat.value}</p>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Classification / BMI / Biometrics — one compact card, stacked rows, each
          full-width justified (label left, value right) per the §0 design principle. */}
      <Card className="border-none shadow-sm">
        <CardContent className="divide-y divide-border p-0">
          <div className="flex items-center justify-between gap-3 p-3">
            <span className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
              Classification
            </span>
            <ClientClassificationBadges
              client={client}
              allPostureAnalyses={allPostureAnalyses?.filter((a) => a.clientId === client.id)}
              emptyLabel="Awaiting assessment"
            />
          </div>

          <div className="flex items-center justify-between gap-3 p-3">
            <span className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
              BMI
            </span>
            <div className="flex items-baseline gap-2">
              <span className={`text-lg font-bold ${bmiColor}`}>
                {bmi > 0 ? bmi.toFixed(1) : '—'}
              </span>
              {bmiCategory && (
                <Badge variant="outline" className="text-xs">
                  {bmiCategory}
                </Badge>
              )}
            </div>
          </div>

          <div className="flex items-center justify-between gap-3 p-3">
            <span className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
              Biometrics
            </span>
            <div className="flex gap-4 text-sm">
              <div className="text-right">
                <span className="text-muted-foreground block text-xs">Height</span>
                <span className="font-semibold">
                  {client.height ? <HeightDisplay value={client.height} precision={0} /> : '—'}
                </span>
              </div>
              <div className="text-right">
                <span className="text-muted-foreground block text-xs">Weight</span>
                <span className="font-semibold">
                  {client.weight ? <WeightDisplay value={client.weight} precision={1} /> : '—'}
                </span>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
